import { Controller, useFormContext } from "react-hook-form";

import { PostgreSQLConnectionAuthMethod } from "src/api/connectApi/types";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { SimpleDropDown } from "src/base-components/SimpleDropDown";
import { CockroachDBConnectionMTLSConfigFields } from "src/connections/config/database/cockroach/CockroachDBConnectionMTLSConfig";
import { CockroachDBRetryConfigFields } from "src/connections/config/database/cockroach/CockroachDBRetryConfigFields";
import { CockroachDBUsernamePasswordAuthFieldset } from "src/connections/config/database/cockroach/CockroachDBUsernamePasswordAuthFieldset";
import { SSHTunnelFieldSet } from "src/connections/config/database/sshTunnel/SSHTunnelFieldSet";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import { errorProps } from "src/connections/config/shared/errorProps";
import { TestInvocationToggle } from "src/connections/config/shared/TestInvocationToggle";
import {
  CockroachDBConnectionConfigInputs,
  Environment,
} from "src/connections/types";

type CockroachDBAuthTypeSelectElement = {
  key: PostgreSQLConnectionAuthMethod;
  value: string;
};

const authMethods: CockroachDBAuthTypeSelectElement[] = [
  { key: "username_password", value: "Username + Password" },
];

const renderAuthFields = (
  authMethod: PostgreSQLConnectionAuthMethod,
  environment: Environment,
) => {
  if (authMethod === "username_password") {
    return (
      <CockroachDBUsernamePasswordAuthFieldset environment={environment} />
    );
  }
};

type EnvironmentConfigProps = {
  environment: Environment;
  labelsPrefix: string;
};

export const CockroachDBEnvironmentConfig: React.FC<EnvironmentConfigProps> = ({
  environment,
  labelsPrefix,
}) => {
  const {
    formState: { errors },
    register,
    watch,
    control,
  } = useFormContext<CockroachDBConnectionConfigInputs>();
  const envPrefix = `${environment}Config` as const;
  const envErrors = errors[envPrefix];
  const enableNonProdConfigs = watch("enableNonProdConfigs");
  const authMethod = watch(`${envPrefix}.authMethod`);
  return (
    <>
      <FormItem
        gap="sm"
        helpTooltipBody={
          <div>
            The name of the CockroachDB database to connect to. When in doubt,
            ask your organization's database administrator.
          </div>
        }
        label={labelsPrefix ? `${labelsPrefix} database name` : "Database name"}
        isRequired
      >
        {isFieldErrored(envErrors, "databaseName") && (
          <ErrorHint>{envErrors?.databaseName?.message}</ErrorHint>
        )}
        <Input
          data-loc={`${environment}-cockroachdb-connection-database-name`}
          placeholder="e.g. cockroach"
          fullWidth
          {...errorProps(isFieldErrored(envErrors, "databaseName"))}
          {...register(`${envPrefix}.databaseName`, {
            required: "Database name is required",
          })}
        />
      </FormItem>
      <FormItem
        gap="sm"
        helpTooltipBody={
          <div>
            The address (hostname or IP) of the server where your CockroachDB
            database is hosted. When in doubt, ask your organization's database
            administrator.
          </div>
        }
        label={labelsPrefix ? `${labelsPrefix} host` : "Host"}
        isRequired
      >
        {isFieldErrored(envErrors, "host") && (
          <ErrorHint>{envErrors?.host?.message}</ErrorHint>
        )}
        <Input
          data-loc={`${environment}-cockroachdb-connection-host`}
          placeholder="e.g. mycluster.cockroachlabs.cloud"
          fullWidth
          {...errorProps(isFieldErrored(envErrors, "host"))}
          {...register(`${envPrefix}.host`, {
            required: "Database host is required",
          })}
        />
      </FormItem>
      <FormItem
        gap="sm"
        helpTooltipBody={
          <div>
            The port number through which the Database Connector will
            communicate with the CockroachDB database. If left blank, this
            setting defaults to 26257.
          </div>
        }
        label={labelsPrefix ? `${labelsPrefix} port` : "Port"}
      >
        {isFieldErrored(envErrors, "port") && (
          <ErrorHint>{envErrors?.port?.message}</ErrorHint>
        )}
        <Input
          data-loc={`${environment}-cockroachdb-connection-port`}
          placeholder="e.g. 26257"
          type="number"
          fullWidth
          {...errorProps(isFieldErrored(envErrors, "port"))}
          {...register(`${envPrefix}.port`)}
        />
      </FormItem>
      <FormItem
        description="The authentication mechanism required for the database"
        gap="sm"
        label="Select authentication type"
        isRequired
      >
        <Controller
          control={control}
          name={`${envPrefix}.authMethod`}
          render={(props) => (
            <SimpleDropDown
              buttonClassName="pl-3"
              buttonDataLoc={`${environment}-cockroachdb-connection-auth-type`}
              className="h-8 w-full"
              elements={authMethods}
              itemsWidth="w-full"
              placeholder="Select authentication type"
              placement="bottomLeft"
              selectedKey={String(props.field.value)}
              onSelect={(value) => {
                props.field.onChange(value);
              }}
            />
          )}
        />
      </FormItem>
      {renderAuthFields(authMethod, environment)}
      <CockroachDBConnectionMTLSConfigFields environment={environment} />
      <SSHTunnelFieldSet environment={environment} />
      <CockroachDBRetryConfigFields environment={environment} />
      {environment === "production" && (
        <TestInvocationToggle<CockroachDBConnectionConfigInputs>
          control={control}
          enableNonProdConfigs={enableNonProdConfigs}
          type="database"
        />
      )}
    </>
  );
};
